import { X } from "lucide-react";
import { type ReactNode, useEffect, useId, useLayoutEffect, useState } from "react";
import { cn } from "../../lib/cn";
import { Button } from "./button";
import { Overlay } from "./overlay";

export type ModalSize = "sm" | "md" | "lg" | "fill";

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-xl",
  fill: "max-w-full",
};

const MODAL_EXIT_MS = 160;

interface ModalShellProps {
  backdropLabel?: string;
  children: ReactNode;
  closeLabel?: string;
  description?: ReactNode;
  dismissDisabled?: boolean;
  footer?: ReactNode;
  icon?: ReactNode;
  onClose(): void;
  open: boolean;
  role?: "alertdialog" | "dialog";
  size: ModalSize;
  /** Secondary line under the title, e.g. the profile a rename applies to. */
  subtitle?: ReactNode;
  title: string;
}

/** The single presentation for centred modal and confirmation surfaces. */
export function ModalShell({
  backdropLabel = "Cancel",
  children,
  closeLabel = "Close",
  description,
  dismissDisabled,
  footer,
  icon,
  onClose,
  open,
  role,
  size,
  subtitle,
  title,
}: ModalShellProps) {
  const titleId = useId();
  const descriptionId = useId();
  const [present, setPresent] = useState(open);
  const [entered, setEntered] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;
    setPresent(true);
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (open || !present) return;
    setEntered(false);
    const timer = globalThis.setTimeout(() => setPresent(false), MODAL_EXIT_MS);
    return () => globalThis.clearTimeout(timer);
  }, [open, present]);

  const transitionClass = open
    ? "[transition:opacity_var(--motion-base),transform_var(--motion-base)]"
    : "[transition:opacity_var(--motion-fast),transform_var(--motion-fast)]";

  return (
    <Overlay
      ariaDescribedBy={description ? descriptionId : undefined}
      ariaLabelledBy={titleId}
      backdropClassName={cn(
        "absolute inset-0 bg-[var(--color-overlay)]",
        open
          ? "[transition:opacity_var(--motion-base)]"
          : "[transition:opacity_var(--motion-fast)]",
        entered ? "opacity-100" : "opacity-0",
      )}
      backdropLabel={backdropLabel}
      dismissDisabled={dismissDisabled}
      onClose={onClose}
      onTransitionEnd={(event) => {
        if (!open && event.currentTarget === event.target && event.propertyName === "opacity") {
          setPresent(false);
        }
      }}
      open={open || present}
      present={open || present}
      role={role}
      rootClassName="fixed inset-0 z-50 isolate flex items-center justify-center px-[var(--dialog-viewport-x)] py-[var(--dialog-viewport-y)]"
      surfaceClassName={cn(
        "relative flex max-h-[calc(100dvh-2*var(--dialog-viewport-y))] w-full origin-center flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)] outline-none motion-reduce:scale-100",
        transitionClass,
        entered ? "scale-100 opacity-100" : "scale-[0.96] opacity-0",
        SIZE_CLASS[size],
      )}
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[var(--color-border)] px-[var(--layout-card-pad-x)] py-[var(--layout-card-pad-y)]">
        <div className="flex min-w-0 items-start gap-3">
          {icon}
          <div className="min-w-0">
            <h2
              className="type-panel-title text-[var(--color-text)] [overflow-wrap:anywhere]"
              id={titleId}
            >
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-0.5 text-sm text-[var(--color-text-secondary)]">{subtitle}</p>
            ) : null}
            {description ? (
              <p
                className="mt-2 text-sm leading-6 text-[var(--color-text-secondary)]"
                id={descriptionId}
              >
                {description}
              </p>
            ) : null}
          </div>
        </div>
        <Button
          aria-label={closeLabel}
          data-overlay-close="true"
          disabled={dismissDisabled}
          iconOnly
          onClick={onClose}
          variant="ghost"
        >
          <X aria-hidden="true" className="size-4" />
        </Button>
      </div>
      <div className="min-h-0 overflow-y-auto overscroll-contain">{children}</div>
      {footer ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-[var(--color-border)] bg-[var(--color-surface-2)] px-[var(--layout-card-pad-x)] py-[var(--layout-card-pad-y)]">
          {footer}
        </div>
      ) : null}
    </Overlay>
  );
}
